/* FlowOfFundsDiagram — visual flow-of-funds for the Company Overview /
 * diligence page. DDA structure: U.S. Bank issues a dedicated Demand Deposit
 * Account (virtual DDA) for every counterparty, so funds are segregated at the
 * bank — not commingled in a single FBO master account. Verqo orchestrates the
 * moves; it never custodies customer funds in its operating account.
 *
 *   1. U.S. BANK · the regulated bank that issues + holds every DDA (top)
 *   2. PER-COUNTERPARTY VIRTUAL DDAs · one real account per party (middle)
 *   3. External counterparty banks · buyer / seller / lender / insurer (bottom)
 *
 * Animated dots flow down through the layers to convey "live money in motion."
 */
import {
  Banknote, Wallet, Landmark, ShieldCheck, BarChart3, Coins,
  Store, ShoppingBasket, Building,
} from "lucide-react";

const DDA_ACCOUNTS = [
  { icon: Banknote,    label: "Seller DDA",        sub: "per-seller virtual account" },
  { icon: Wallet,      label: "Buyer DDA",         sub: "per-buyer virtual account" },
  { icon: Landmark,    label: "Lender DDA",        sub: "loan capital account" },
  { icon: ShieldCheck, label: "Insurance DDA",     sub: "Allianz reserves" },
  { icon: BarChart3,   label: "Platform Ops DDA",  sub: "fee accruals" },
  { icon: Coins,       label: "Treasury DDA",      sub: "float / yield" },
];

const EXTERNAL_BANKS = [
  { icon: ShoppingBasket, label: "Buyer banks",  sub: "ACH debit on due date" },
  { icon: Store,          label: "Seller banks", sub: "ACH / RTP credit on payout" },
  { icon: Building,       label: "Lender bank",  sub: "capital wires in/out" },
  { icon: Building,       label: "Insurer bank", sub: "claim wires in" },
];

function ConnectorLane({ label = "via U.S. Bank", flip = false }) {
  return (
    <div className="relative h-12 mx-auto w-full max-w-[640px]">
      <svg viewBox="0 0 640 48" className="w-full h-full" preserveAspectRatio="none">
        <line x1="320" y1="0" x2="320" y2="48" stroke="rgba(220,255,58,0.4)" strokeWidth="1" strokeDasharray="2 4"/>
        <circle r="3" fill="#dcff3a">
          <animateMotion dur="2.2s" repeatCount="indefinite"
                         path={flip ? "M320 48 L320 0" : "M320 0 L320 48"}/>
          <animate attributeName="opacity" values="0;1;1;0"
                   keyTimes="0;0.1;0.9;1" dur="2.2s" repeatCount="indefinite"/>
        </circle>
        <circle r="3" fill="#dcff3a" opacity="0.6">
          <animateMotion dur="2.2s" repeatCount="indefinite" begin="1.1s"
                         path={flip ? "M320 48 L320 0" : "M320 0 L320 48"}/>
          <animate attributeName="opacity" values="0;0.6;0.6;0"
                   keyTimes="0;0.1;0.9;1" dur="2.2s" repeatCount="indefinite" begin="1.1s"/>
        </circle>
      </svg>
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <span className="bg-[#0c1612] px-2.5 py-0.5 rounded-full text-[9.5px] uppercase tracking-[0.22em] text-lime-300 font-semibold">
          {label}
        </span>
      </div>
    </div>
  );
}

export default function FlowOfFundsDiagram() {
  return (
    <div className="relative rounded-3xl bg-[#0c1612] text-white p-6 md:p-10 overflow-hidden" data-testid="flow-of-funds-diagram">
      {/* Grid texture + radial glow */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}/>
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ background: "radial-gradient(circle at 50% 50%, rgba(220,255,58,0.25) 0%, transparent 60%)" }}/>

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="text-[10.5px] uppercase tracking-[0.26em] text-lime-300 font-semibold">
            Flow of funds · live
          </div>
          <div className="inline-flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse"/> Streaming
          </div>
        </div>

        {/* LAYER 1 — U.S. Bank */}
        <div className="mx-auto max-w-[640px]">
          <div className="rounded-2xl border-2 border-white/30 bg-white/[0.04] backdrop-blur-sm p-5 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 mx-auto mb-3">
              <Building className="w-6 h-6 text-white" strokeWidth={1.6}/>
            </div>
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-white/60 font-semibold">Regulated bank</div>
            <div className="text-[19px] md:text-[22px] font-semibold tracking-tight mt-1">U.S. Bank</div>
            <div className="text-[12px] text-white/55 mt-1.5 max-w-md mx-auto">
              Issues + holds a dedicated DDA for every counterparty · originates ACH / RTP / wire · no customer funds touch Verqo's operating account
            </div>
          </div>
        </div>

        {/* Connector */}
        <ConnectorLane label="via U.S. Bank"/>

        {/* LAYER 2 — Per-counterparty virtual DDAs */}
        <div className="rounded-2xl border-2 border-lime-300/60 bg-lime-300/[0.04] p-5 md:p-6">
          <div className="text-center mb-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-lime-300 font-semibold">U.S. Bank · virtual DDAs</div>
            <div className="text-[19px] md:text-[22px] font-semibold tracking-tight">A dedicated Demand Deposit Account per counterparty</div>
            <div className="text-[12px] text-white/55 mt-1.5">
              Real bank-issued account numbers · true per-party segregation · same-day ACH + RTP + wire origination · SOC 2 Type II.
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {DDA_ACCOUNTS.map(s => (
              <div key={s.label} className="rounded-xl border border-white/10 bg-white/[0.04] p-3 flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-md bg-lime-300/15 text-lime-300 grid place-items-center shrink-0">
                  <s.icon className="w-3.5 h-3.5" strokeWidth={1.8}/>
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold text-white truncate">{s.label}</div>
                  <div className="text-[10px] text-white/45 truncate">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Connector */}
        <ConnectorLane label="ACH / RTP / Wire"/>

        {/* LAYER 3 — External banks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {EXTERNAL_BANKS.map(b => (
            <div key={b.label} className="rounded-xl border border-white/15 bg-white/[0.03] p-3.5 text-center">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 mx-auto mb-2">
                <b.icon className="w-4 h-4 text-white/80" strokeWidth={1.6}/>
              </div>
              <div className="text-[11.5px] font-semibold text-white">{b.label}</div>
              <div className="text-[9.5px] text-white/50 mt-0.5 leading-tight">{b.sub}</div>
            </div>
          ))}
        </div>

        {/* Caption + link */}
        <div className="mt-6 pt-5 border-t border-white/10 text-center">
          <p className="text-[11.5px] text-white/55 max-w-2xl mx-auto leading-relaxed">
            Every counterparty DDA is issued and held at U.S. Bank, so balances are segregated at the bank itself. Hash-chained audit trail on every wallet_txn. Daily 2-way reconciliation between U.S. Bank's ledger and Verqo's mirror.
          </p>
          <a href="/api/banking-docs/file/01_flow_of_funds.md"
             target="_blank" rel="noopener noreferrer"
             className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-lime-300 hover:text-lime-200 underline">
            Read the full flow-of-funds doc →
          </a>
        </div>
      </div>
    </div>
  );
}
